import orderService from "../services/order.service";
import { toogleErrorMessage, toogleSuccessMessage } from "./alertMessage";
import {
  ORDER_DETAIL,
  ORDER_DETAIL_ERROR,
  ORDER_DETAIL_LOADING,
  ORDER_ERROR,
  ORDER_LIST,
  ORDER_LOADING,
  ORDER_RESPONSE_ERROR,
  ORDER_RESPONSE_LOADING,
  ORDER_STATUS_UPDATE_ERROR,
  ORDER_STATUS_UPDATE_LOADING,
} from "./type";

const getResponseMessage = (response) =>
  response?.data?.message ||
  response?.data?.data?.message ||
  response?.response?.data?.message ||
  response?.response?.data?.data?.message ||
  "";

const getOrderListData = (response) => {
  const responseBody = response?.data || {};
  const containers = [responseBody?.data?.data, responseBody?.data, responseBody];
  const items = containers
    .map((container) => {
      if (Array.isArray(container)) return container;
      return (
        container?.orders ||
        container?.items ||
        container?.results ||
        container?.list ||
        container?.data
      );
    })
    .find(Array.isArray) || [];
  const data = containers.find(
    (container) => container && typeof container === "object" && !Array.isArray(container),
  ) || {};
  const pagination = containers
    .map((container) => container?.pagination || container?.meta)
    .find(Boolean) || {};
  const counts = containers
    .map((container) => container?.counts || container?.statistics || container?.stats)
    .find((value) => value && typeof value === "object") || {};

  return { data, items, pagination, counts, responseBody };
};
const getOrderDetailData = (response) => {
  const responseBody = response?.data || {};
  const data =
    responseBody?.data?.order ||
    responseBody?.data?.item ||
    responseBody?.data?.data ||
    responseBody?.data ||
    responseBody?.order ||
    responseBody?.item ||
    responseBody;

  return Array.isArray(data) ? data[0] || null : data;
};

export const getOrderListAction =
  ({ page = 1, limit = 10, status = "all" } = {}) =>
  async (dispatch) => {
    dispatch({ type: ORDER_LOADING, payload: true });

    try {
      const response = await orderService.getOrders({ page, limit, status });

      if (response?.status >= 200 && response?.status < 300) {
        const { data, items, pagination, counts, responseBody } = getOrderListData(response);
        const reportedTotal =
          pagination.total ??
          pagination.totalCount ??
          pagination.totalItems ??
          pagination.totalRecords ??
          pagination.totalDocs ??
          pagination.count ??
          data?.total ??
          data?.totalCount ??
          data?.totalItems ??
          data?.totalRecords ??
          data?.totalDocs ??
          responseBody?.total ??
          responseBody?.totalCount ??
          responseBody?.totalDocs;
        const pageSize = Number(
          pagination.limit ??
            pagination.pageSize ??
            pagination.perPage ??
            pagination.per_page ??
            data?.limit ??
            limit,
        ) || Number(limit);
        const currentPage =
          Number(
            pagination.page ??
              pagination.currentPage ??
              pagination.current_page ??
              data?.page ??
              responseBody?.page ??
              page,
          ) || Number(page);
        const reportedTotalPages = Number(
          pagination.totalPages ??
            pagination.total_pages ??
            pagination.pages ??
            data?.totalPages ??
            responseBody?.totalPages,
        );
        const total =
          reportedTotal !== undefined && reportedTotal !== null
            ? Number(reportedTotal) || 0
            : reportedTotalPages
              ? Math.max((reportedTotalPages - 1) * pageSize + items.length, items.length)
              : items.length;
        const totalPages =
          reportedTotalPages || Math.max(Math.ceil(total / pageSize), 1);
        dispatch({
          type: ORDER_LIST,
          payload: {
            items,
            counts,
            pagination: {
              ...pagination,
              page: currentPage,
              limit: pageSize,
              total,
              totalPages,
            },
          },
        });
        dispatch({ type: ORDER_ERROR, payload: "" });
      } else {
        dispatch({
          type: ORDER_ERROR,
          payload: getResponseMessage(response) || "Unable to load orders.",
        });
      }

      return response;
    } finally {
      dispatch({ type: ORDER_LOADING, payload: false });
    }
  };

export const getOrderAction = (id) => async (dispatch) => {
  dispatch({ type: ORDER_DETAIL_LOADING, payload: true });
  dispatch({ type: ORDER_DETAIL, payload: null });
  dispatch({ type: ORDER_DETAIL_ERROR, payload: "" });

  try {
    const response = await orderService.getOrder(id);

    if (response?.status >= 200 && response?.status < 300) {
      const order = getOrderDetailData(response);
      if (order && typeof order === "object") {
        dispatch({ type: ORDER_DETAIL, payload: order });
      } else {
        dispatch({ type: ORDER_DETAIL_ERROR, payload: "Order details were not found." });
      }
    } else {
      dispatch({
        type: ORDER_DETAIL_ERROR,
        payload: getResponseMessage(response) || "Unable to load order.",
      });
    }

    return response;
  } finally {
    dispatch({ type: ORDER_DETAIL_LOADING, payload: false });
  }
};

export const updateOrderStatusAction = (id, payload) => async (dispatch) => {
  dispatch({ type: ORDER_STATUS_UPDATE_LOADING, payload: true });
  dispatch({ type: ORDER_STATUS_UPDATE_ERROR, payload: "" });

  try {
    const response = await orderService.updateOrderStatus(id, payload);
    const status = response?.status ?? response?.response?.status;
    const message = getResponseMessage(response);

    if (status >= 200 && status < 300) {
      toogleSuccessMessage(message || "Order status updated successfully.");
      return { success: true, response };
    }

    const errorMessage = message || "Unable to update order status.";
    dispatch({ type: ORDER_STATUS_UPDATE_ERROR, payload: errorMessage });
    toogleErrorMessage(errorMessage);
    return { success: false, response };
  } catch (error) {
    const errorMessage =
      getResponseMessage(error) || error?.message || "Unable to update order status.";
    dispatch({ type: ORDER_STATUS_UPDATE_ERROR, payload: errorMessage });
    toogleErrorMessage(errorMessage);
    return { success: false, error };
  } finally {
    dispatch({ type: ORDER_STATUS_UPDATE_LOADING, payload: false });
  }
};

export const respondToOrderAction = (id, payload) => async (dispatch) => {
  dispatch({ type: ORDER_RESPONSE_LOADING, payload: true });
  dispatch({ type: ORDER_RESPONSE_ERROR, payload: "" });

  try {
    const response = await orderService.respondToOrder(id, payload);
    const status = response?.status ?? response?.response?.status;
    const message = getResponseMessage(response);

    if (status >= 200 && status < 300) {
      toogleSuccessMessage(message || "Order response submitted successfully.");
      return { success: true, response };
    }

    const errorMessage = message || "Unable to submit your order response.";
    dispatch({ type: ORDER_RESPONSE_ERROR, payload: errorMessage });
    toogleErrorMessage(errorMessage);
    return { success: false, response };
  } catch (error) {
    const errorMessage =
      getResponseMessage(error) || error?.message || "Unable to submit your order response.";
    dispatch({ type: ORDER_RESPONSE_ERROR, payload: errorMessage });
    toogleErrorMessage(errorMessage);
    return { success: false, error };
  } finally {
    dispatch({ type: ORDER_RESPONSE_LOADING, payload: false });
  }
};
